import type {
  NotificationPreferences,
  NotificationPreferencesPatch,
  NotificationSummary,
} from '@tabbio/types';
import { api } from './api';

export async function fetchNotifications(
  cursor?: string,
  limit?: number,
  type?: string,
  signal?: AbortSignal,
): Promise<{ notifications: NotificationSummary[]; nextCursor: string | null }> {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  if (limit) params.set('limit', String(limit));
  if (type) params.set('type', type);
  const qs = params.toString();
  const res = await api.get<NotificationSummary[]>(
    `/api/notifications${qs ? `?${qs}` : ''}`,
    { signal },
  );
  if (res.error) throw new Error(res.error.message);
  return {
    notifications: res.data ?? [],
    nextCursor: (res as any).meta?.nextCursor ?? null,
  };
}

export async function fetchNotificationUnreadCount(): Promise<number> {
  const res = await api.get<{ count: number }>('/api/notifications/unread-count');
  if (res.error) throw new Error(res.error.message);
  return res.data?.count ?? 0;
}

export async function fetchNotificationPreferences(): Promise<NotificationPreferences> {
  const res = await api.get<NotificationPreferences>('/api/notifications/preferences');
  if (res.error) throw new Error(res.error.message);
  if (!res.data) throw new Error('Notification preferences unavailable');
  return res.data;
}

export async function updateNotificationPreferences(
  patch: NotificationPreferencesPatch,
): Promise<NotificationPreferences> {
  const res = await api.patch<NotificationPreferences>(
    '/api/notifications/preferences',
    patch,
  );
  if (res.error) throw new Error(res.error.message);
  if (!res.data) throw new Error('Notification preferences unavailable');
  return res.data;
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  const res = await api.post(`/api/notifications/${notificationId}/read`);
  if (res.error) throw new Error(res.error.message);
}

export async function markAllNotificationsRead(): Promise<void> {
  const res = await api.post('/api/notifications/read-all');
  if (res.error) throw new Error(res.error.message);
}

export async function deleteNotification(notificationId: string): Promise<void> {
  const res = await api.delete(`/api/notifications/${notificationId}`);
  if (res.error) throw new Error(res.error.message);
}

export async function registerDeviceToken(
  token: string,
  platform: 'ios' | 'android' | 'web',
): Promise<void> {
  const res = await api.post('/api/notifications/device-tokens', { token, platform });
  if (res.error) throw new Error(res.error.message);
}

export async function unregisterDeviceToken(token: string): Promise<void> {
  const res = await api.post('/api/notifications/device-tokens/unregister', { token });
  if (res.error) throw new Error(res.error.message);
}
